import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BASE_URL } from "../utilities/BASE_URL";
import pupDisplayName from "../components/pupCards/pupDisplayName";

// The Power-Up card catalog: every card's display data (name/color/modifier/description/
// flavor/artKey) plus play gates, keyed by code. The backend is the single source of truth
// (GET /cards/catalog, public — served straight from the PupCardType enum); gameplay
// messages carry ONLY card codes, and every renderer resolves them against this slice.
// Cached in localStorage by the server's content-hash version, so a reload paints cards
// instantly from the cache while the boot fetch revalidates in the background; a version
// change (server-side card patch) swaps the catalog in and rewrites the cache.

// Versioned storage key: bump the suffix if the cached shape ever changes, so an old
// cache can never deserialize into garbage.
const STORAGE_KEY = "pupCatalog:v1";

// Floor between fetch attempts. Also what stops the unknown-code self-heal (PupCard
// refetching when it meets a code the catalog lacks) from looping when the code is
// genuinely unknown.
const REFETCH_THROTTLE_MS = 10_000;

// Every consumer reads its display name from here, so this is where the catalog's
// bookkeeping number is dropped off a repeated card's name ("Psychic 2" -> "Psychic",
// "Cancel2" -> "Cancel"); see pupDisplayName.js. The entry is COPIED rather than mutated so
// the raw server copy is what lands in the localStorage cache — the rule then re-applies on
// the next load instead of being baked into stale cached data.
const buildByCode = (cards) => {
    const byCode = {};
    for (const card of cards) {
        if (card && card.code) byCode[card.code] = { ...card, name: pupDisplayName(card.name) };
    }
    return byCode;
};

// Synchronous cache read for initialState, so the first render already has card data.
const loadCachedCatalog = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const { version, cards } = JSON.parse(raw);
        if (!version || !Array.isArray(cards)) return null;
        return { version, byCode: buildByCode(cards) };
    } catch {
        return null;
    }
};

// Plain fetch, not fetchWithToken: the endpoint is public and this runs at app boot,
// before any login. Pass { force: true } to signal an out-of-band refetch (unknown code
// self-heal) — both paths share the same throttle below.
export const fetchPupCatalog = createAsyncThunk(
    "pupCatalog/fetch",
    async (_arg, { rejectWithValue }) => {
        try {
            const response = await fetch(`${BASE_URL}/cards/catalog`);
            if (!response.ok) {
                return rejectWithValue(`Catalog fetch failed (${response.status})`);
            }
            const data = await response.json();
            if (!data || !data.version || !Array.isArray(data.cards)) {
                return rejectWithValue("Malformed catalog response");
            }
            return { version: data.version, cards: data.cards };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    },
    {
        // One in-flight fetch at a time, and at most one attempt per throttle window —
        // boot + login both dispatch this and dedupe here, and a genuinely unknown code
        // can't spin the self-heal into a request loop.
        condition: (_arg, { getState }) => {
            const { status, lastFetchAt } = getState().pupCatalog;
            if (status === "loading") return false;
            return Date.now() - lastFetchAt >= REFETCH_THROTTLE_MS;
        },
    }
);

const cached = loadCachedCatalog();

const pupCatalogSlice = createSlice({
    name: "pupCatalog",
    initialState: {
        version: cached ? cached.version : null,
        byCode: cached ? cached.byCode : {},   // { EXTRA_CARD: { code, name, color, ... } }
        status: cached ? "ready" : "idle",     // idle | loading | ready | error
        lastFetchAt: 0,                        // epoch ms of the last fetch ATTEMPT (throttle anchor)
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchPupCatalog.pending, (state) => {
                state.status = "loading";
                state.lastFetchAt = Date.now();
            })
            .addCase(fetchPupCatalog.fulfilled, (state, action) => {
                const { version, cards } = action.payload;
                state.status = "ready";
                if (version !== state.version) {
                    state.version = version;
                    state.byCode = buildByCode(cards);
                    try {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify({ version, cards }));
                    } catch {
                        // Storage full/blocked: the in-memory catalog still works; only the
                        // instant-render-on-reload optimization is lost.
                    }
                }
            })
            .addCase(fetchPupCatalog.rejected, (state) => {
                // Keep serving a cached catalog if we have one; only flag error when the
                // client has nothing at all to render cards from.
                state.status = state.version ? "ready" : "error";
            });
    },
});

export const selectPupCatalog = (state) => state.pupCatalog.byCode;
export const selectPupCard = (state, code) => state.pupCatalog.byCode[code];

export default pupCatalogSlice.reducer;
